/**
 * Shared Clerk appearance for Command (dark shell).
 *
 * Official docs (Clerk appearance variables, 2025+):
 * https://clerk.com/docs/react/guides/customizing-clerk/appearance-prop/variables
 *
 * Use current tokens (deprecated names are ignored by recent Clerk UI):
 *   colorForeground          ← text (was colorText)
 *   colorMutedForeground     ← secondary text (was colorTextSecondary)
 *   colorPrimaryForeground   ← text on primary (was colorTextOnPrimaryBackground)
 *   colorInput / colorInputForeground  ← (was colorInputBackground / colorInputText)
 *   colorNeutral             ← borders, hover, nav chrome (dark theme uses white)
 *
 * @clerk/themes `dark` sets colorForeground: white, colorNeutral: white.
 * We keep that and only brand colorPrimary to orange.
 */
import { dark } from "@clerk/themes";

/** Match @clerk/themes dark + brand orange — see dark.mjs variables */
export const commandClerkVariables = {
  // From official dark theme
  colorBackground: "#09090b",
  colorNeutral: "white",
  colorForeground: "white",
  colorInputForeground: "white",
  colorInput: "#18181b",
  colorMutedForeground: "#a1a1aa",
  // Brand
  colorPrimary: "#f97316",
  // Text on solid orange buttons (not nav pills)
  colorPrimaryForeground: "#431407",
  colorDanger: "#f87171",
  colorSuccess: "#34d399",
  colorWarning: "#fbbf24",
  borderRadius: "0.75rem",
  fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
} as const;

const orangePrimary = {
  formButtonPrimary:
    "bg-gradient-to-b from-orange-400 to-orange-600 !text-orange-950 font-semibold shadow-lg shadow-orange-500/25 hover:brightness-110",
  membersPageInviteButton:
    "bg-gradient-to-b from-orange-400 to-orange-600 !text-orange-950 font-semibold",
} as const;

/**
 * Global appearance for ClerkProvider — every modal/popover inherits this.
 * Prefer `baseTheme` (still supported by @clerk/themes) + variables from docs.
 */
export const commandClerkAppearance = {
  baseTheme: dark,
  variables: commandClerkVariables,
  elements: {
    rootBox: "mx-auto w-full",
    card: "!bg-zinc-950 !border !border-white/10 shadow-2xl shadow-orange-500/10",
    modalContent: "!bg-zinc-950",
    modalCloseButton: "!text-zinc-400 hover:!text-white",
    headerTitle: "!text-white",
    headerSubtitle: "!text-zinc-400",
    socialButtonsBlockButton:
      "!border !border-white/10 !bg-white/5 !text-white hover:!bg-white/10",
    ...orangePrimary,
    formFieldInput:
      "!border-white/10 !bg-zinc-900 !text-white focus:!border-orange-500/50",
    formFieldLabel: "!text-zinc-400",
    formFieldHintText: "!text-zinc-500",
    formFieldSuccessText: "!text-emerald-400",
    formFieldErrorText: "!text-red-400",
    footerActionLink: "!text-orange-400 hover:!text-orange-300",
    identityPreviewEditButton: "!text-orange-400",
    identityPreviewText: "!text-white",
    identityPreviewSecondaryIdentifier: "!text-zinc-400",
    dividerLine: "!bg-white/10",
    dividerText: "!text-zinc-500",
    // UserButton popover
    userButtonPopoverCard:
      "!bg-zinc-950 !border !border-white/10 !shadow-2xl !shadow-black/60",
    userButtonPopoverMain: "!bg-zinc-950",
    userButtonPopoverActions: "!bg-zinc-950",
    userButtonPopoverActionButton: "!text-white hover:!bg-white/5",
    userButtonPopoverActionButtonText: "!text-white",
    userButtonPopoverActionButtonIcon: "!text-zinc-400",
    userButtonPopoverFooter: "!hidden",
    userPreviewMainIdentifier: "!text-white",
    userPreviewSecondaryIdentifier: "!text-zinc-400",
    // Organization switcher
    organizationSwitcherTrigger:
      "!bg-zinc-900 !text-white border border-white/15 rounded-full px-2.5 py-1 text-xs font-medium hover:!bg-zinc-800",
    organizationSwitcherTriggerIcon: "!text-zinc-300",
    organizationPreviewMainIdentifier: "!text-white !font-semibold",
    organizationPreviewSecondaryIdentifier: "!text-zinc-400",
    organizationSwitcherPopoverCard:
      "!bg-zinc-950 !border !border-white/10 !shadow-2xl",
    organizationSwitcherPopoverMain: "!bg-zinc-950",
    organizationSwitcherPopoverActionButton: "!text-white hover:!bg-white/5",
    organizationSwitcherPopoverActionButtonText: "!text-white",
    organizationSwitcherPopoverActionButtonIcon: "!text-zinc-400",
    // Profile modals — left nav uses colorForeground via variables; reinforce
    navbar: "!bg-zinc-950 !border-r !border-white/10",
    navbarButton: "!text-white",
    navbarButtonIcon: "!text-zinc-400",
    scrollBox: "!bg-zinc-950",
    pageScrollBox: "!bg-zinc-950",
    profileSection: "!border-white/10",
    profileSectionTitle: "!border-white/10",
    profileSectionTitleText: "!text-white",
    profileSectionContent: "!text-zinc-200",
    profileSectionPrimaryButton: "!text-orange-400",
    profileSectionItem: "!text-white",
    profileSectionItemButton: "!text-white",
    accordionTriggerButton: "!text-white",
    tableHead: "!text-zinc-400",
    tableBody: "!text-white",
    badge: "!text-white !bg-white/10",
    profilePage: "!text-white",
    breadcrumbsItem: "!text-zinc-400",
    breadcrumbsItemDivider: "!text-zinc-600",
    menuButton: "!text-white hover:!bg-white/5",
    menuList: "!bg-zinc-950 !border-white/10",
    menuItem: "!text-white hover:!bg-white/5",
    formButtonReset: "!text-zinc-300 hover:!text-white",
  },
} as const;

export const authClerkAppearance = commandClerkAppearance;

export const orgSwitcherAppearance = {
  ...commandClerkAppearance,
  elements: {
    ...commandClerkAppearance.elements,
    rootBox: "flex items-center",
    organizationSwitcherTrigger:
      "!bg-zinc-900 !text-white border border-white/15 rounded-full px-2.5 py-1 text-xs font-medium hover:!bg-zinc-800 focus:!shadow-none",
    organizationPreviewMainIdentifier:
      "!text-white !text-xs !font-semibold max-w-[9rem] truncate",
    organizationPreviewSecondaryIdentifier: "!text-[10px] !text-zinc-400",
  },
} as const;

export const orgProfileAppearance = {
  ...commandClerkAppearance,
  elements: {
    ...commandClerkAppearance.elements,
    rootBox: "w-full",
    cardBox: "!bg-zinc-950 !shadow-2xl",
    card: "!bg-zinc-950 !border-0",
    navbar: "!bg-zinc-950 !border-r !border-white/10",
    navbarButton: "!text-white",
    navbarButtonIcon: "!text-zinc-400",
  },
} as const;

export const userProfileAppearance = {
  ...commandClerkAppearance,
  elements: {
    ...commandClerkAppearance.elements,
    rootBox: "w-full",
    cardBox: "!bg-zinc-950 !shadow-2xl",
    card: "!bg-zinc-950 !border-0",
    navbar: "!bg-zinc-950 !border-r !border-white/10",
    navbarButton: "!text-white",
    navbarButtonIcon: "!text-zinc-400",
  },
} as const;

export const userButtonAppearance = {
  ...commandClerkAppearance,
  elements: {
    ...commandClerkAppearance.elements,
    avatarBox: "h-7 w-7",
    userButtonPopoverFooter: "!hidden",
  },
} as const;
